import React, { useEffect, useState } from "react";

import locationService from "@plannel/services/data/location-service";
import { Autocomplete, TextField } from "@mui/material"
import debug from "@plannel/utils/debug-util";
import reduxUtil from "@plannel/utils/redux-util";
import { useDispatch } from "react-redux";
import { isEmpty, isEqual } from "lodash";
import { useTranslation } from "react-i18next";

import codeUtil from "@plannel/pages/utils/code-util";

const REDUX_LOCATION_FILTER = "LOCATION_FILTER";
const LOCATION_TYPE_MAP = codeUtil.locationType;

const LOCATION_TYPE = {
  PLANT: LOCATION_TYPE_MAP.PLANT.cd,
  DC: LOCATION_TYPE_MAP.DC.cd,
  WHSE: LOCATION_TYPE_MAP.WHSE.cd,
  STO: LOCATION_TYPE_MAP.STO.cd,
  CUST: LOCATION_TYPE_MAP.CUST.cd,
  VENDOR: LOCATION_TYPE_MAP.VENDOR.cd,
  VIRTUAL_DC: LOCATION_TYPE_MAP.VIRTUAL_DC.cd
}

const IP_LOCATION_TYPE = [
  LOCATION_TYPE.DC,
  LOCATION_TYPE.WHSE,
  LOCATION_TYPE.STO,
  LOCATION_TYPE.CUST,
  LOCATION_TYPE.VENDOR,
  LOCATION_TYPE.PLANT
]

/* DC, WHSE, STO, CUST, VENDOR, in-house true */
const IPLocationFilter = ({ viewName, locationValue, onChangeFilter }) => {
  return <LocationFilter viewName={viewName} locationValue={locationValue} onChangeFilter={onChangeFilter} locTypeFilters={IP_LOCATION_TYPE} inhouseFilters={[true]} />
}

const LocationFilter = ({ viewName, locationValue, onChangeFilter, locTypeFilters, inhouseFilters, title="location" }) => {
  const { t } = useTranslation();

  const reduxDispatch = useDispatch();
  const locationDataListReduxState = reduxUtil.getViewState(REDUX_LOCATION_FILTER);

  const [locationDataList, setLocationDataList] = useState(locationDataListReduxState?.[title] ? locationDataListReduxState?.[title] : []);
  const [locationFilterDataList, setLocationFilterDataList] = useState([]);

  /* Location Type 정렬 PLANT -> DC -> WHSE -> STO -> CUST -> VENDOR */
  const sortLocationResults = (locResults) => {
    const plantLocations = [];
    const dcLocations = [];
    const whseLocations = [];
    const stoLocations = [];
    const custLocations = [];
    const vendorLocations = [];
    const typeNotFound = [];

    for (const loc of locResults) {
      const { locType } = loc;

      switch (locType) {
        case LOCATION_TYPE_MAP.PLANT.cd:
          plantLocations.push(loc);
          break;
        case LOCATION_TYPE_MAP.DC.cd:
          dcLocations.push(loc);
          break;
        case LOCATION_TYPE_MAP.WHSE.cd:
          whseLocations.push(loc);
          break;
        case LOCATION_TYPE_MAP.STO.cd:
          stoLocations.push(loc);
          break;
        case LOCATION_TYPE_MAP.CUST.cd:
          custLocations.push(loc);
          break;
        case LOCATION_TYPE_MAP.VENDOR.cd:
          vendorLocations.push(loc);
          break;
        case LOCATION_TYPE_MAP.VIRTUAL_DC.cd:
          dcLocations.push(loc);
          break;
        default:
          typeNotFound.push(loc);
          break;
      }
    }

    return [...plantLocations, ...dcLocations, ...whseLocations, ...stoLocations, ...custLocations, ...vendorLocations, ...typeNotFound];
  }

  const onChangeHandler = (_, value) => {
    onChangeFilter(value);

    const createLocationReduxState = reduxUtil.createObjectState(viewName, { [title]: value });
    reduxDispatch(reduxUtil.updateViewState(createLocationReduxState));
  }

  /* PLANT, CDC, RDC, DC, VENDOR */
  const locationTypeFilter = (results) => {
    let resultsFilter = [];
    
    if (!isEmpty(locTypeFilters) || !isEmpty(inhouseFilters)) {
      for (const result of results) {
        if (!isEmpty(locTypeFilters) && !locTypeFilters.includes(result.locType)) {
          continue;
        }
        if (!isEmpty(inhouseFilters) && !inhouseFilters.includes(result.inhouseFlg)) {
          continue;
        }
        resultsFilter.push(result);
      }
    } else {
      resultsFilter = results;
    }

    /* 기억 하고있는 redux data가 제거되거나 변경되는경우 dropdown list에 데이터가 존재하는지 체크하는 변수 */
    const existSelectedValueInResults = resultsFilter.find(result => isEqual(result.locCd, locationValue?.locCd));

    if (isEmpty(existSelectedValueInResults)) {
      onChangeHandler(null, null);
    } else {
      onChangeHandler(null, existSelectedValueInResults);
    }

    return sortLocationResults(resultsFilter);
  }

  const getLocationData = () => {
    /* 이전에 불러온 Dropdown data list가 존재하는경우 다시 가져오지 않음. */
    if (isEmpty(locationDataListReduxState?.[title])) {
      let locationResultData = [];
      
      locationService.getLookup()
        .then(response => {
          const { results } = response.data;
          if (!isEmpty(results)) {
            locationResultData = results;
          }

          const filterLocationList = locationTypeFilter(locationResultData);
          setLocationFilterDataList(filterLocationList);
          setLocationDataList(locationResultData);
        })
        .catch(e => {
          debug(e);
        })
        .then(() => {
          const createLocationDataListReduxState = reduxUtil.createObjectState(REDUX_LOCATION_FILTER, { [title]: locationResultData });
          reduxDispatch(reduxUtil.updateViewState(createLocationDataListReduxState));
        });
    } else {
      setLocationFilterDataList(locationTypeFilter(locationDataList));
    }
  }

  useEffect(() => {
    getLocationData();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <Autocomplete
      key={title}
      name={title}
      options={locationFilterDataList}
      getOptionLabel={option => option?.name}
      value={locationValue}
      onChange={onChangeHandler}
      isOptionEqualToValue={(option, value) => option.locCd === value?.locCd}
      size="small"
      sx={{ mr: 1, minWidth: 240 }}
      renderInput={(params) => (
        <TextField {...params} label={t(title)} />
      )}
    />
  )
}

const reloadLocationFilter = (reduxDispatch) => {
  const locationReduxFilterState = reduxUtil.getViewState(REDUX_LOCATION_FILTER);

  reduxDispatch(reduxUtil.removeViewState(locationReduxFilterState));
}

export default React.memo(LocationFilter);
export { reloadLocationFilter, IPLocationFilter, LOCATION_TYPE };